/**
 * The lens area (ui/DESIGN.md §3.5): the one lens open under the map, in place. Records (a node), runs (an
 * arrow), changes, values: each wired to the stage's data and to the peeks. While a lens's data is on its
 * way the frame shows the same layout as shimmering rows; never a spinner.
 */
import type { Execution, RecordList, RecordRow, Tile } from '../lib/api.ts';
import { LensFrame } from './lenses/common.tsx';
import { RecordsLens, type RecordFilter } from './lenses/RecordsLens.tsx';
import { RunsLens } from './lenses/RunsLens.tsx';
import { ChangesLens } from './lenses/ChangesLens.tsx';
import { ValuesLens, type ValuePick, type ValueStep } from './lenses/ValuesLens.tsx';
import { mergeFacets } from './lenses/facets.ts';
import type { MessageRef } from './lenses/common.tsx';
import type { StageData } from './useStageData.ts';
import type { LensId, Step } from './scope.ts';
import { trailValues } from './scope.ts';
import { noun } from '../design/primitives.tsx';

/** The records stuck at a step, by the step's data model (the index is the list's business). */
export interface StepFilter { cls: string; state: 'missing' | 'pending' }

export function StageLens({ lens, data, steps, kind, process, failedOnly, filter, expandedRun, extra, changesAll, values, loadExecution, onClose, onKind, onProcess, onFailedOnly, onNarrow, onExpandRun, onExtra, onChangesAll, onShowAll, onPick, onValue, onRecord, onRun, onMessage }: {
  lens: LensId;
  data: StageData;
  steps: Step[];
  kind: string | null;
  process: string | null;
  failedOnly: boolean;
  filter: StepFilter | null;
  /** Lens-internal view state, which the page keeps in the URL. */
  expandedRun: string | null;
  extra: number;
  changesAll: boolean;
  values: { all: boolean; common: boolean; onChange: (change: { all?: boolean; common?: boolean }) => void };
  loadExecution: (id: string) => Promise<Execution>;
  onClose: () => void;
  onKind: (cls: string) => void;
  onProcess: (p: string | null) => void;
  onFailedOnly: (v: boolean) => void;
  onNarrow: (f: StepFilter | null) => void;
  onExpandRun: (id: string | null) => void;
  onExtra: (extra: number) => void;
  onChangesAll: (all: boolean) => void;
  onShowAll: () => void;
  onPick: (step: ValueStep, from: ValuePick) => void;
  onValue: (tile: Tile) => void;
  onRecord: (row: RecordRow, list: RecordList) => void;
  onRun: (executionId: string) => void;
  onMessage: (ref: MessageRef) => void;
}) {
  if (lens === 'records') {
    const list = data.list;
    if (!list) return <Skeleton kind="record" title={kind ? noun(kind, 2) : 'records'} onClose={onClose} rows={6} />;
    const i = filter ? list.steps.findIndex((st) => st.cls === filter.cls) : -1;
    const f: RecordFilter | null = filter && i >= 0 ? { step: i, state: filter.state } : null;
    return (
      <RecordsLens list={list} kinds={data.kinds} runs={data.runs?.list ?? []} onKind={onKind} onOpen={onRecord} filter={f} extra={extra} onExtra={onExtra}
        onNarrow={(x) => onNarrow(x ? { cls: list.steps[x.step].cls, state: x.state } : null)}
        onShowAll={list.truncated ? onShowAll : undefined} onClose={onClose} />
    );
  }
  if (lens === 'runs') {
    const runs = data.runs;
    if (!runs) return <Skeleton kind="run" title="runs" onClose={onClose} rows={5} />;
    return (
      <RunsLens runs={runs.list} processes={runs.processes} process={process} failedOnly={failedOnly} expanded={expandedRun} onExpand={onExpandRun} total={runs.runs}
        onRun={(id) => onRun(id)} onMessage={onMessage} loadExecution={loadExecution} onProcess={onProcess} onFailedOnly={onFailedOnly} onClose={onClose} />
    );
  }
  if (lens === 'changes') {
    const history = data.history;
    if (!history) return <Skeleton kind="document" title="what changed" onClose={onClose} rows={4} />;
    return <ChangesLens history={history} onMessage={onMessage} onClose={onClose} every={changesAll} onEvery={onChangesAll} />;
  }
  const o = data.overview;
  if (!o) return <Skeleton title="characteristic values" onClose={onClose} rows={3} grid />;
  return (
    <ValuesLens wall={o.wall} facets={mergeFacets(o.facets).filter((f) => !steps.some((st) => st.l.toLowerCase() === f.label.toLowerCase()))} hidden={trailValues(steps)} view={values}
      records={new Map(data.kinds.map((k) => [k.cls, k.records]))} onPick={onPick} onValue={onValue} onClose={onClose} />
  );
}

/** The frame with the shape of what is coming: rows of a table or a grid of attributes. */
function Skeleton({ kind, title, onClose, rows, grid }: { kind?: 'run' | 'document' | 'record'; title: string; onClose: () => void; rows: number; grid?: boolean }) {
  return (
    <LensFrame kind={kind} title={title} onClose={onClose}>
      {grid ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-x-6 gap-y-4">
          {Array.from({ length: rows * 3 }, (_, i) => (
            <div key={i} className="flex flex-col gap-2">
              <span className="h-3 w-24 rounded-full mv-shimmer" style={{ animationDelay: `${i * 40}ms` }} />
              <span className="h-8 w-40 rounded-chip mv-shimmer" style={{ animationDelay: `${i * 40 + 60}ms` }} />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="flex items-center gap-4 h-11 border-b border-n4">
              <span className="h-3.5 w-28 rounded-full mv-shimmer" style={{ animationDelay: `${i * 50}ms` }} />
              <span className="h-3.5 w-20 rounded-full mv-shimmer" style={{ animationDelay: `${i * 50 + 30}ms` }} />
              <span className="h-3.5 w-16 rounded-full mv-shimmer" style={{ animationDelay: `${i * 50 + 60}ms` }} />
              <span className="ml-auto h-3.5 w-24 rounded-full mv-shimmer" style={{ animationDelay: `${i * 50 + 90}ms` }} />
            </div>
          ))}
        </div>
      )}
    </LensFrame>
  );
}
